/**
 * Server-side conflict rules (SPEC §12), applied to a transaction before it runs against the
 * authoritative graph. They turn ops that were valid on the client's state but not on the server's
 * into ops that keep the user's intent and lose no data:
 *
 * - Edit vs delete: an edit (content, title, field, tags, collapse, move) of a deleted node
 *   restores it first; the edit wins. The node gets a `sys:restored` marker field (conflict UI in
 *   Phase 3).
 * - Create or move into a deleted parent: re-attached at the end of the nearest live ancestor. A
 *   create into a page or day that is deleted entirely brings back that page or day alone.
 * - A move that would create a cycle (two devices moving nodes into each other): skipped.
 * - Delete of a deleted node, restore of a live one: skipped (idempotent).
 * - Creating a journal day that already exists (deterministic ids): skipped.
 * - Creating a page whose title another live page has (two devices linked the same new title):
 *   the duplicate is created as an untitled tombstone pointing at the existing page
 *   (`sys:mergedInto`), and every op naming it, in this transaction or any later one, is
 *   redirected there. Its tags and fields are added to the existing page. The text refers to the
 *   title, so it resolves to the existing page anyway.
 * - A rename also relinks referrers the client did not know about (refs added on another device).
 * - A relink of a node that is no longer live: skipped (it is not an edit, so nothing is restored).
 * - A restore that would bring back a title another live node has now: the restored node gets
 *   "Title (restored)".
 *
 * Any other duplicate title (a rename to a taken title) rejects the transaction.
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { MAX_TITLE } from './model.ts'
import { op, type OpBody } from './ops.ts'
import { keysBetween } from './order.ts'
import { scanReferrers } from './refs.ts'
import type { Rand } from './rng.ts'
import { hasTitle, titleKey, titleOwner } from './titles.ts'

export const RESTORED_FIELD = 'sys:restored'
/** On the tombstone of a duplicate page: the page it was merged into. */
export const MERGED_FIELD = 'sys:mergedInto'

/** Where ops naming `id` go: itself, or the page a merged duplicate points to (chains followed). */
export function mergedTarget(g: Graph, id: NodeId): NodeId {
  let cur = id
  for (let i = 0; i < 16; i++) {
    const n = g.get(cur)
    const next = n && n.deletedAt !== null ? n.props[MERGED_FIELD] : undefined
    if (typeof next !== 'string') return cur
    cur = next
  }
  return cur
}

export type Resolution =
  | { readonly ok: true; readonly ops: OpBody[]; readonly inverses: OpBody[]; readonly notes: string[] }
  | { readonly ok: false; readonly reason: string }

/**
 * Apply `ops` to `g` as one transaction, rewriting them by the rules above where they no longer
 * fit the graph. Returns the ops actually applied (to log and broadcast) and their inverses.
 * On rejection nothing is applied (everything done so far is rolled back).
 */
export function applyWithConflicts(g: Graph, ops: readonly OpBody[], ts: number, rand: Rand): Resolution {
  const out: OpBody[] = []
  const notes: string[] = []
  const applied: OpBody[] = [] // inverses of what has been applied, for rollback and for the caller
  const tryApply = (o: OpBody): boolean => {
    try {
      applied.push(g.apply(o, ts))
      out.push(o)
      return true
    } catch {
      return false
    }
  }
  const nearestLiveAncestor = (id: NodeId): NodeId | null => {
    for (let cur: NodeId | null = id; cur !== null; cur = g.get(cur)?.parentId ?? null) {
      if (g.isLive(cur)) return cur
    }
    return null
  }
  /** Restore `id` and any deleted ancestors, top-down, each on its own (not their old subtrees). */
  const restoreChain = (id: NodeId): boolean => {
    const chain: NodeId[] = []
    for (let cur: NodeId | null = id; cur !== null && !g.isLive(cur); cur = g.get(cur)?.parentId ?? null) {
      if (!g.has(cur)) return false
      chain.push(cur)
    }
    for (const n of chain.reverse()) {
      if (!tryApply(op.restore(n, [n]))) return false
      tryApply(op.prop(n, RESTORED_FIELD, ts))
      dedupeTitles([n])
    }
    if (chain.length) notes.push(`restored ${id} (edited after delete)`)
    return true
  }
  const lowerTitleTaken = (title: string, except: NodeId): boolean => titleOwner(g, title, except) !== undefined
  /** Restored titled nodes whose title is taken by now get "Title (restored)". */
  function dedupeTitles(ids: readonly NodeId[]): void {
    for (const id of ids) {
      const n = g.get(id)
      if (!n || !hasTitle(n) || !lowerTitleTaken(n.title!, id)) continue
      tryApply(op.title(id, freeTitle(g, n.title!)))
      notes.push(`restored ${id} renamed: "${n.title}" is taken`)
    }
  }
  const mapId = (id: NodeId): NodeId => mergedTarget(g, id)

  // Exact rollback on rejection: tracked prior records, so a created node vanishes entirely.
  g.beginTrack()
  let tracking = true
  const rollback = (): void => {
    if (!tracking) return
    tracking = false
    g.restoreRecords(g.endTrack())
  }

  try {
    for (let idx = 0; idx < ops.length; idx++) {
      const o = remapOp(ops[idx]!, mapId)
      switch (o.type) {
        case 'node.create': {
          const n = o.payload.node
          const existing = g.get(n.id)
          if (existing) {
            if (existing.kind === 'journal' && n.kind === 'journal' && existing.date === n.date) {
              if (!g.isLive(n.id)) restoreChain(n.id)
              notes.push(`journal ${n.date} already exists`)
              continue
            }
            rollback()
            return { ok: false, reason: `node ${n.id} already exists` }
          }
          const owner = n.title !== null && n.kind !== 'block' ? titleOwner(g, n.title, n.id) : undefined
          if (owner) {
            if ((n.kind === 'page' || n.kind === 'tag') && n.parentId === null) {
              const stub = { ...n, title: null, content: '', tags: [], props: { [MERGED_FIELD]: owner.id } }
              if (!tryApply(op.create(stub)) || !tryApply(op.delete(n.id))) {
                rollback()
                return { ok: false, reason: `merge of ${n.id} failed` }
              }
              const tags = [...owner.tags, ...n.tags.filter((t) => !owner.tags.includes(t))]
              if (tags.length !== owner.tags.length) tryApply(op.tags(owner.id, tags))
              for (const [field, value] of Object.entries(n.props)) {
                if (!Object.prototype.hasOwnProperty.call(owner.props, field)) tryApply(op.prop(owner.id, field, value))
              }
              notes.push(`"${n.title}" exists: ${n.id} merged into ${owner.id}`)
              continue
            }
            rollback()
            return { ok: false, reason: `title "${n.title}" is taken` }
          }
          if (n.parentId !== null && !g.isLive(n.parentId)) {
            let target = nearestLiveAncestor(n.parentId)
            if (target === null) {
              // The whole page or day is deleted: the new content wins and brings back the top
              // node alone (marked restored), not the deleted subtree.
              const top = topAncestor(g, n.parentId)
              if (top === null || !restoreChain(top)) {
                rollback()
                return { ok: false, reason: `no live ancestor for ${n.id}` }
              }
              target = top
            }
            const order = endKey(g, target, rand)
            notes.push(`created ${n.id} under live ancestor ${target}`)
            if (!tryApply(op.create({ ...n, parentId: target, order }))) {
              rollback()
              return { ok: false, reason: `create ${n.id} failed` }
            }
            continue
          }
          if (!tryApply(o)) {
            rollback()
            return { ok: false, reason: `create ${n.id} failed` }
          }
          continue
        }
        case 'node.move': {
          const { parentId } = o.payload
          if (!g.has(o.nodeId)) {
            rollback()
            return { ok: false, reason: `move of unknown node ${o.nodeId}` }
          }
          if (!g.isLive(o.nodeId) && !restoreChain(o.nodeId)) {
            rollback()
            return { ok: false, reason: `cannot restore ${o.nodeId}` }
          }
          let target = parentId
          let order = o.payload.order
          if (!g.isLive(target)) {
            const live = g.has(target) ? nearestLiveAncestor(target) : null
            if (live === null) {
              notes.push(`skipped move of ${o.nodeId}: target gone`)
              continue
            }
            target = live
            order = endKey(g, target, rand)
            notes.push(`moved ${o.nodeId} to live ancestor ${target}`)
          }
          if (createsCycle(g, o.nodeId, target)) {
            notes.push(`skipped move of ${o.nodeId}: would create a cycle`)
            continue
          }
          if (g.children(target).some((k) => k !== o.nodeId && g.get(k)!.order === order)) order = endKey(g, target, rand)
          // Unchanged ops pass through as the same object, so the client recognises its own op.
          const final = target === parentId && order === o.payload.order ? o : op.move(o.nodeId, target, order)
          if (!tryApply(final)) {
            rollback()
            return { ok: false, reason: `move ${o.nodeId} failed` }
          }
          continue
        }
        case 'node.delete': {
          if (!g.isLive(o.nodeId)) {
            notes.push(`skipped delete of ${o.nodeId}: already deleted`)
            continue
          }
          tryApply(o)
          continue
        }
        case 'node.restore': {
          const ids = o.payload.ids.filter((id) => g.has(id) && !g.isLive(id))
          if (!ids.includes(o.nodeId)) {
            notes.push(`skipped restore of ${o.nodeId}: already live`)
            continue
          }
          const parent = g.get(o.nodeId)!.parentId
          if (parent !== null && !g.isLive(parent) && !ids.includes(parent)) restoreChain(parent)
          const final = ids.length === o.payload.ids.length ? o : op.restore(o.nodeId, ids)
          if (!tryApply(final)) {
            rollback()
            return { ok: false, reason: `restore ${o.nodeId} failed` }
          }
          dedupeTitles(ids)
          continue
        }
        case 'node.title': {
          if (!g.has(o.nodeId)) {
            rollback()
            return { ok: false, reason: `unknown node ${o.nodeId}` }
          }
          if (o.payload.title !== null && lowerTitleTaken(o.payload.title, o.nodeId)) {
            rollback()
            return { ok: false, reason: `title "${o.payload.title}" is taken` }
          }
          if (!g.isLive(o.nodeId) && !restoreChain(o.nodeId)) {
            rollback()
            return { ok: false, reason: `cannot restore ${o.nodeId}` }
          }
          const before = g.get(o.nodeId)!.title
          if (!tryApply(o)) {
            rollback()
            return { ok: false, reason: `title ${o.nodeId} failed` }
          }
          const after = o.payload.title
          if (before !== null && after !== null && before !== after) {
            // Relink referrers this transaction does not cover (refs added on another device).
            const key = titleKey(before)
            const covered = new Set<NodeId>()
            for (const later of ops.slice(idx + 1)) {
              if (later.type === 'node.relink' && titleKey(later.payload.from) === key) covered.add(mapId(later.nodeId))
            }
            for (const r of scanReferrers(g, before)) {
              if (covered.has(r)) continue
              tryApply(op.relink(r, before, after))
              notes.push(`relinked ${r}`)
            }
          }
          continue
        }
        case 'node.relink': {
          if (!g.isLive(o.nodeId)) {
            notes.push(`skipped relink of ${o.nodeId}: not live`)
            continue
          }
          if (!tryApply(o)) {
            rollback()
            return { ok: false, reason: `relink ${o.nodeId} failed` }
          }
          continue
        }
        default: {
          // content, props, tags, collapse: the edit wins over a delete.
          if (!g.has(o.nodeId)) {
            rollback()
            return { ok: false, reason: `unknown node ${o.nodeId}` }
          }
          if (!g.isLive(o.nodeId) && !restoreChain(o.nodeId)) {
            rollback()
            return { ok: false, reason: `cannot restore ${o.nodeId}` }
          }
          if (!tryApply(o)) {
            rollback()
            return { ok: false, reason: `${o.type} ${o.nodeId} failed` }
          }
        }
      }
    }
  } catch (e) {
    rollback()
    return { ok: false, reason: e instanceof Error ? e.message : String(e) }
  }
  if (tracking) g.endTrack()
  return { ok: true, ops: out, inverses: applied, notes }
}

/** The op with ids of skipped duplicates replaced; the same object when nothing changes. */
function remapOp(o: OpBody, map: (id: NodeId) => NodeId): OpBody {
  const nodeId = map(o.nodeId)
  switch (o.type) {
    case 'node.create': {
      const p = o.payload.node.parentId
      const parentId = p === null ? null : map(p)
      return parentId === p ? o : op.create({ ...o.payload.node, parentId })
    }
    case 'node.move': {
      const parentId = map(o.payload.parentId)
      return nodeId === o.nodeId && parentId === o.payload.parentId ? o : op.move(nodeId, parentId, o.payload.order)
    }
    case 'node.restore': {
      const ids = o.payload.ids.map(map)
      return nodeId === o.nodeId && ids.every((x, i) => x === o.payload.ids[i]) ? o : op.restore(nodeId, ids)
    }
    default:
      return nodeId === o.nodeId ? o : ({ ...o, nodeId } as OpBody)
  }
}

/** "Title (restored)", then "Title (restored 2)", …: the first title no live node has. */
function freeTitle(g: Graph, title: string): string {
  const base = title.slice(0, MAX_TITLE - 20)
  for (let i = 1; ; i++) {
    const t = i === 1 ? `${base} (restored)` : `${base} (restored ${i})`
    if (!titleOwner(g, t)) return t
  }
}

/** The top-level ancestor of `id` (a page, day or tag), or null if the chain is broken. */
function topAncestor(g: Graph, id: NodeId): NodeId | null {
  let cur = id
  for (;;) {
    const n = g.get(cur)
    if (!n) return null
    if (n.parentId === null) return cur
    cur = n.parentId
  }
}

function createsCycle(g: Graph, nodeId: NodeId, target: NodeId): boolean {
  for (let cur: NodeId | null = target; cur !== null; cur = g.get(cur)?.parentId ?? null) {
    if (cur === nodeId) return true
  }
  return false
}

function endKey(g: Graph, parentId: NodeId, rand: Rand): string {
  const ks = g.children(parentId)
  const last = ks[ks.length - 1]
  return keysBetween(last === undefined ? null : g.get(last)!.order, null, 1, rand)[0]!
}
